/**
 * Customer Web Push Subscription Helper · v2.7.65+E93
 * ═══════════════════════════════════════════════════════════════════
 * Mirror of `driverPush.js` for riders. Registers the service worker,
 * prompts for notification permission, and posts the resulting
 * subscription up to `/api/customer/push/subscribe`. Best-effort —
 * silent-fail if the browser doesn't support Web Push or the rider
 * denies permission.
 *
 * Browser detection: Safari (macOS/iOS) supports Web Push only when
 * the site runs as a Home Screen PWA. Regular Safari tabs skip the
 * subscribe flow entirely and let SMS/email pick up the slack.
 */
import customerApi from "./customerApi";

// Reuse the driver service worker — it's a plain notification handler
// with no driver-specific logic (see /public/driver-push-sw.js).
const SW_URL = "/driver-push-sw.js";
const LOCAL_KEY = "rtg_customer_push_subscribed";

// ─── Browser detection ─────────────────────────────────────────────────
export function detectBrowser() {
  if (typeof window === "undefined") return { family: "ssr", isPWA: false, pushSupported: false };
  const ua = navigator.userAgent || "";
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isFirefox = /Firefox\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);
  const family = isEdge ? "edge" : isChrome ? "chrome" : isFirefox ? "firefox" : isSafari ? "safari" : "other";
  const isPWA = (
    window.matchMedia?.("(display-mode: standalone)").matches
    || (navigator.standalone === true)
    || document.referrer.startsWith("android-app://")
  );
  const pushSupported = (
    "serviceWorker" in navigator
    && "PushManager" in window
    && (family !== "safari" || isPWA)
  );
  return { family, isPWA, pushSupported };
}

// ─── VAPID key helper ──────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const raw = atob((base64String + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

// ─── Subscribe flow ────────────────────────────────────────────────────
/**
 * Call from the rider Profile tab (Enable Push button) or from the
 * booking form. Idempotent — returns the existing subscription if one
 * already exists. Never throws — errors are logged and returned in the
 * result object so callers can show a friendly toast.
 */
export async function ensureCustomerPushSubscription() {
  const meta = detectBrowser();
  const localState = { subscribed: false, ...meta, reason: null };

  if (!meta.pushSupported) {
    localState.reason = meta.family === "safari" && !meta.isPWA
      ? "safari_not_pwa"
      : "unsupported";
    return localState;
  }

  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      if (perm !== "granted") {
        localState.reason = "permission_" + perm;
        return localState;
      }
      const { data: keyRes } = await customerApi.get("/customer/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(keyRes.public_key);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    const subJson = sub.toJSON();
    await customerApi.post("/customer/push/subscribe", {
      endpoint:       subJson.endpoint,
      keys:           subJson.keys,
      browser_family: meta.family,
      is_pwa:         meta.isPWA,
      push_supported: meta.pushSupported,
    });
    localStorage.setItem(LOCAL_KEY, "1");
    localState.subscribed = true;
    return localState;
  } catch (e) {
    console.warn("[customer-push] subscribe failed:", e?.message || e);
    localState.reason = "exception:" + (e?.name || "unknown");
    return localState;
  }
}

/** Unsubscribe from THIS device. */
export async function unsubscribeCustomerPush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return { ok: false, reason: "no_registration" };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      localStorage.removeItem(LOCAL_KEY);
      return { ok: true, reason: "no_subscription" };
    }
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try {
      await customerApi.delete(`/customer/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`);
    } catch (_e) { /* server-side cleanup best-effort */ }
    localStorage.removeItem(LOCAL_KEY);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "exception:" + (e?.name || "unknown") };
  }
}

/** Read-only test hook — never throws. */
export function isCustomerPushSubscribed() {
  try { return localStorage.getItem(LOCAL_KEY) === "1"; } catch { return false; }
}
